import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Link } from 'react-router-dom';
import {
  Calculator, Star, Building2, Package, AlertTriangle,
  ChevronDown, ChevronUp, Truck, Info
} from 'lucide-react';
import api from '../lib/api';
import { useCart } from '../context/CartContext';
import { isPartnerClickable, PARTNER_ACTIVE_CLASSES, PARTNER_DISABLED_CLASSES, PARTNER_DISABLED_TOOLTIP } from '../lib/partnerPolicy';

const NATURE_COLORS = {
  PERMANENTE: 'text-emerald-700', PRECOMMANDE: 'text-blue-700',
  LANCEMENT: 'text-violet-700', PROMOTION: 'text-red-700',
  SOLDES: 'text-amber-700', PROMO_FLASH: 'text-orange-700',
  DESTOCKAGE: 'text-yellow-700', EVENEMENTIELLE: 'text-pink-700'
};

const fmtPrice = (v) => v != null && v > 0 ? `${Number(v).toFixed(2).replace('.', ',')} \u20ac` : '\u2014';

/**
 * ComparisonTable V2 — Tableau de simulation
 * Single unified table, responsive (desktop table / mobile cards),
 * quantity constraints (min/max/multiple), corrected FREE_UNITS labels
 */
const ComparisonTable = ({ productId, productName, productImage }) => {
  const [quantity, setQuantity] = useState(6);
  const [inputVal, setInputVal] = useState('6');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qtyError, setQtyError] = useState(null);

  // Once data loads, adjust default qty to respect constraints of first distributor
  const didAdjust = React.useRef(false);

  const loadComparison = useCallback(async (q) => {
    if (q < 1) return;
    setLoading(true);
    try {
      const res = await api.get(`/products/${productId}/compare-distributions?quantity=${q}`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const q = parseInt(inputVal) || 0;
      if (q >= 1) {
        setQuantity(q);
        loadComparison(q);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [inputVal, loadComparison]);

  useEffect(() => { loadComparison(quantity); }, []);

  // Auto-adjust quantity when first data loads to respect constraints
  useEffect(() => {
    if (data?.comparisons?.length && !didAdjust.current) {
      didAdjust.current = true;
      const firstComp = data.comparisons[0];
      const cst = firstComp?.quantity_constraints || {};
      const min = cst.min_quantity || 1;
      const mult = cst.multiple || 1;
      const defaultQty = Math.max(min, mult, 1);
      if (defaultQty !== quantity) {
        setQuantity(defaultQty);
        setInputVal(String(defaultQty));
      }
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const comparisons = data?.comparisons || [];
  const bestOverall = data?.best_overall;

  // Validate quantity against all distribution constraints
  const qtyWarnings = useMemo(() => {
    if (!comparisons.length) return [];
    const warnings = [];
    comparisons.forEach(c => {
      const cst = c.quantity_constraints || {};
      const name = c.partner_nom;
      if (cst.min_quantity && quantity < cst.min_quantity) {
        warnings.push(`${name} : min ${cst.min_quantity} unit\u00e9s`);
      }
      if (cst.max_quantity && quantity > cst.max_quantity) {
        warnings.push(`${name} : max ${cst.max_quantity} unit\u00e9s`);
      }
      if (cst.multiple && cst.multiple > 1 && quantity % cst.multiple !== 0) {
        warnings.push(`${name} : multiple de ${cst.multiple} requis`);
      }
    });
    return warnings;
  }, [comparisons, quantity]);

  // Flatten all rows for the unified table
  const allRows = useMemo(() => {
    const rows = [];
    comparisons.forEach(comp => {
      (comp.rows || []).forEach((row, ri) => {
        rows.push({ ...row, comp, isFirstInDist: ri === 0, distRowCount: (comp.rows || []).length });
      });
    });
    return rows;
  }, [comparisons]);

  return (
    <div className="rounded-xl border-2 border-slate-200 bg-white overflow-hidden" data-testid="comparison-table">

      {/* HEADER */}
      <div className="px-5 py-4 bg-slate-50 border-b border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-slate-600" />
          <h3 className="text-base font-bold text-slate-900" data-testid="sim-title">Tableau de simulation</h3>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs font-semibold text-slate-500 whitespace-nowrap">Quantit&eacute; :</Label>
          <Input
            type="number" min="1" step="1" value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            className={`w-24 h-9 text-lg font-bold text-center tabular-nums ${qtyWarnings.length > 0 ? 'border-amber-400 ring-1 ring-amber-200' : ''}`}
            data-testid="comparison-quantity"
          />
        </div>
      </div>

      {/* QUANTITY WARNINGS */}
      {qtyWarnings.length > 0 && (
        <div className="px-5 py-2 bg-amber-50 border-b border-amber-100 flex items-start gap-2" data-testid="qty-warnings">
          <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-700 space-y-0.5">
            {qtyWarnings.map((w, i) => <p key={i}>{w}</p>)}
          </div>
        </div>
      )}

      {/* LOADING */}
      {loading && (
        <div className="flex items-center justify-center py-10 gap-2 text-sm text-slate-400">
          <div className="animate-spin h-4 w-4 border-2 border-slate-400 border-t-transparent rounded-full" />
          Chargement...
        </div>
      )}

      {/* EMPTY */}
      {!loading && comparisons.length === 0 && (
        <div className="px-5 py-10 text-center">
          <Package className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-400">Aucune distribution disponible.</p>
        </div>
      )}

      {/* BEST OVERALL BANNER — info only, no auto-selection */}
      {!loading && bestOverall && (
        <div className="mx-4 mt-4 px-4 py-2.5 rounded-lg bg-slate-50 border border-slate-200 flex items-center gap-2" data-testid="comparison-best-overall">
          <Info className="h-4 w-4 text-slate-500 flex-shrink-0" />
          <p className="text-sm text-slate-600">
            <span className="font-medium">Meilleur prix effectif</span> : {fmtPrice(bestOverall.effective_unit_price)}/u
            {bestOverall.partner_nom && <span className="ml-1 text-slate-500">chez {bestOverall.partner_nom}</span>}
          </p>
        </div>
      )}

      {/* === DESKTOP TABLE (hidden on mobile) === */}
      {!loading && allRows.length > 0 && (
        <div className="hidden md:block overflow-x-auto m-4" data-testid="sim-desktop-table">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-100 border-b-2 border-slate-200">
                <th className="text-left text-[10px] font-bold text-slate-500 uppercase px-3 py-2.5 whitespace-nowrap sticky left-0 bg-slate-100 z-20 min-w-[140px]">Distributeur</th>
                <th className="text-right text-[10px] font-bold text-slate-500 uppercase px-3 py-2.5 whitespace-nowrap">Prix unit. base</th>
                <th className="text-left text-[10px] font-bold text-slate-500 uppercase px-3 py-2.5 whitespace-nowrap min-w-[180px]">Offre</th>
                <th className="text-right text-[10px] font-bold text-slate-500 uppercase px-3 py-2.5 whitespace-nowrap">Qt&eacute; Totale</th>
                <th className="text-right text-[10px] font-bold text-slate-500 uppercase px-3 py-2.5 whitespace-nowrap">Mnt Total</th>
                <th className="text-right text-[10px] font-bold text-slate-500 uppercase px-3 py-2.5 whitespace-nowrap">Prix unit. effectif</th>
                <th className="text-center text-[10px] font-bold text-slate-500 uppercase px-3 py-2.5 whitespace-nowrap w-[80px]">Action</th>
              </tr>
            </thead>
            <tbody>
              {allRows.map((row, ri) => (
                <DesktopRow key={`${row.comp.distribution_id}-${row.mechanic_id || 'base'}-${ri}`} row={row} productId={productId} productName={productName} productImage={productImage} quantity={quantity} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* === MOBILE CARDS (hidden on desktop) === */}
      {!loading && comparisons.length > 0 && (
        <div className="md:hidden p-3 space-y-3" data-testid="sim-mobile-cards">
          {comparisons.map(comp => (
            <MobileDistCard key={comp.distribution_id} comp={comp} productId={productId} productName={productName} productImage={productImage} quantity={quantity} />
          ))}
        </div>
      )}
    </div>
  );
};


/** Desktop table row — each row has its own Add button (Phase 4D: explicit mechanic choice) */
const DesktopRow = ({ row, productId, productName, productImage, quantity }) => {
  const { comp, isFirstInDist, distRowCount } = row;
  const { addItem } = useCart();
  const isBest = row.is_best;
  const isApplicable = row.applicable;
  const isBase = row.mechanic_id === null;
  const nat = row.nature_offre;
  const [adding, setAdding] = useState(false);

  // Validate qty for this specific distributor
  const cst = comp.quantity_constraints || {};
  const minQ = cst.min_quantity || 1;
  const mult = cst.multiple || 1;
  const maxQ = cst.max_quantity;
  const qtyValid = quantity >= minQ && (mult <= 1 || quantity % mult === 0) && (!maxQ || quantity <= maxQ);

  const handleAddRow = async () => {
    if (!qtyValid || !isApplicable) return;
    setAdding(true);
    await addItem({
      product_id: productId, product_name: productName, product_image: productImage,
      distribution_id: comp.distribution_id, partner_id: comp.partner_id,
      partner_nom: comp.partner_nom, base_price: comp.base_price,
      quantity,
      min_qty: minQ, max_qty: maxQ, multiple: mult,
      mechanic_id: row.mechanic_id || null,
    });
    setAdding(false);
  };

  let rowCls = 'border-b border-slate-100 transition-colors';
  if (isBest && !isBase) rowCls += ' bg-indigo-50/60';
  else if (!isApplicable) rowCls += ' opacity-35';

  return (
    <tr className={rowCls} data-testid={`sim-row-${row.mechanic_id || 'base'}`}>
      {/* Distributeur — spans rows */}
      {isFirstInDist && (
        <td className="px-3 py-2.5 align-top sticky left-0 bg-white z-10 border-r border-slate-100" rowSpan={distRowCount}>
          <div className="flex flex-col gap-1">
            {isPartnerClickable(comp) && comp.partner_slug ? (
              <Link to={`/partner/${comp.partner_slug}`} className={`text-sm font-bold truncate ${PARTNER_ACTIVE_CLASSES}`} data-testid={`sim-dist-link-${comp.distribution_id?.substring(0,8)}`}>
                {comp.partner_nom}
              </Link>
            ) : (
              <span className={`text-sm font-bold truncate ${comp.partner_slug ? PARTNER_DISABLED_CLASSES : 'text-slate-900'}`} title={comp.partner_slug && !isPartnerClickable(comp) ? PARTNER_DISABLED_TOOLTIP : undefined}>{comp.partner_nom}</span>
            )}
            {comp.logistics_audit?.franco && (
              <span className="text-[10px] text-slate-400">Franco : {comp.logistics_audit.franco} &euro;</span>
            )}
            {comp.logistics_audit?.shipping_rules_count === 0 && (
              <span className="text-[9px] text-amber-600 font-medium" data-testid={`sim-no-shipping-warn-${comp.distribution_id?.substring(0,8)}`}>Pas de r&egrave;gle livraison</span>
            )}
          </div>
        </td>
      )}
      {/* Prix unit. base */}
      <td className="px-3 py-2.5 text-right text-sm text-slate-600 tabular-nums whitespace-nowrap">
        {fmtPrice(comp.base_price)}
      </td>
      {/* Offre — title + nature sub */}
      <td className="px-3 py-2.5 text-left">
        <div className="flex items-center gap-1.5 min-w-0">
          {isBest && !isBase && <Star className="h-3.5 w-3.5 text-indigo-600 fill-indigo-600 flex-shrink-0" />}
          <div className="min-w-0">
            <span className={`text-sm font-medium block truncate ${
              isBase ? 'text-slate-400 italic' : isApplicable ? 'text-slate-900' : 'text-slate-400'
            }`} data-testid={`sim-offer-${row.mechanic_id || 'base'}`}>
              {row.offer_title || row.offer_label}
            </span>
            {!isBase && row.offer_nature && (
              <span className={`text-[10px] ${NATURE_COLORS[nat] || 'text-slate-500'}`}>{row.offer_nature}</span>
            )}
          </div>
          {!isApplicable && !isBase && row.min_quantity_needed && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 border border-amber-200 flex-shrink-0 whitespace-nowrap">
              d&egrave;s {row.min_quantity_needed}
            </span>
          )}
        </div>
      </td>
      {/* Qté Totale + sub "dont x unités offertes" */}
      <td className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap ${isBest && !isBase ? 'font-bold text-slate-900' : 'text-slate-700'}`}
        data-testid={`sim-qty-${row.mechanic_id || 'base'}`}>
        {isApplicable ? (
          <div>
            <span className="text-sm">{row.qty_total}</span>
            {row.qty_sub && <span className="block text-[10px] text-emerald-600 font-normal">{row.qty_sub}</span>}
          </div>
        ) : '\u2014'}
      </td>
      {/* Mnt Total + sub "remise : x€" */}
      <td className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap ${isBest && !isBase ? 'font-bold text-indigo-700' : 'text-slate-700'}`}
        data-testid={`sim-mnt-${row.mechanic_id || 'base'}`}>
        {isApplicable ? (
          <div>
            <span className="text-sm">{fmtPrice(row.mnt_total)}</span>
            {row.mnt_sub && <span className="block text-[10px] text-red-500 font-normal">{row.mnt_sub}</span>}
          </div>
        ) : '\u2014'}
      </td>
      {/* Prix unit. effectif */}
      <td className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap ${
        isBest && !isBase
          ? 'text-base font-black text-indigo-700'
          : isApplicable ? 'text-sm font-semibold text-slate-900' : 'text-sm text-slate-400'
      }`} data-testid={`sim-eff-${row.mechanic_id || 'base'}`}>
        {isApplicable ? fmtPrice(row.effective_unit_price) : '\u2014'}
      </td>
      {/* Action — one Add button per row (only for commercial partners) */}
      <td className="px-3 py-2.5 text-center">
        {isApplicable && comp.partner_is_commercial !== false ? (
          <button type="button" onClick={handleAddRow}
            disabled={adding || !qtyValid}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[10px] font-bold transition-colors
              ${qtyValid
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            title={!qtyValid ? `Quantit\u00e9 invalide (min=${minQ}${mult > 1 ? `, multiple=${mult}` : ''})` : isBase ? 'Ajouter sans offre' : `Ajouter avec: ${row.offer_title}`}
            data-testid={`sim-add-row-${row.mechanic_id || 'base'}`}>
            {adding ? <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" /> : <Calculator className="h-3 w-3" />}
            Ajouter
          </button>
        ) : isApplicable && comp.partner_is_commercial === false ? (
          <span className="text-[9px] text-slate-400 italic" data-testid={`sim-not-commercial-${row.mechanic_id || 'base'}`}>Non partenaire</span>
        ) : null}
      </td>
    </tr>
  );
};


/** Mobile card per distributor — each row has its own Add button (Phase 4D) */
const MobileDistCard = ({ comp, productId, productName, productImage, quantity }) => {
  const [expanded, setExpanded] = useState(true);
  const { addItem } = useCart();
  const rows = comp.rows || [];
  const bestRow = rows.find(r => r.is_best);
  const hasBestMech = bestRow && bestRow.mechanic_id;
  const cst = comp.quantity_constraints || {};
  const minQ = cst.min_quantity || 1;
  const mult = cst.multiple || 1;
  const maxQ = cst.max_quantity;
  const qtyValid = quantity >= minQ && (mult <= 1 || quantity % mult === 0) && (!maxQ || quantity <= maxQ);

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid={`sim-mobile-${comp.distribution_id?.substring(0,8)}`}>
      {/* Header */}
      <button type="button" onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 border-b border-slate-200 text-left">
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <span className="text-sm font-bold text-slate-900 truncate">{comp.partner_nom}</span>
          {hasBestMech && <Star className="h-3.5 w-3.5 text-indigo-600 fill-indigo-600 flex-shrink-0" />}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs font-semibold text-slate-500">{fmtPrice(comp.base_price)}/u</span>
          {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="divide-y divide-slate-100">
          {rows.filter(r => r.applicable).map((row, ri) => (
            <MobileRowCard key={row.mechanic_id || `base-${ri}`} row={row} comp={comp}
              productId={productId} productName={productName} productImage={productImage}
              quantity={quantity} qtyValid={qtyValid} minQ={minQ} mult={mult} />
          ))}
        </div>
      )}
    </div>
  );
};

/** Mobile single row with its own Add button */
const MobileRowCard = ({ row, comp, productId, productName, productImage, quantity, qtyValid, minQ, mult }) => {
  const { addItem } = useCart();
  const [adding, setAdding] = useState(false);
  const isBest = row.is_best;
  const isBase = row.mechanic_id === null;

  const handleAdd = async () => {
    if (!qtyValid) return;
    setAdding(true);
    await addItem({
      product_id: productId, product_name: productName, product_image: productImage,
      distribution_id: comp.distribution_id, partner_id: comp.partner_id,
      partner_nom: comp.partner_nom, base_price: comp.base_price,
      quantity,
      min_qty: minQ, max_qty: comp.quantity_constraints?.max_quantity, multiple: mult,
      mechanic_id: row.mechanic_id || null,
    });
    setAdding(false);
  };

  return (
    <div className={`px-4 py-3 ${isBest && !isBase ? 'bg-indigo-50/50' : ''}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {isBest && !isBase && <Star className="h-3.5 w-3.5 text-indigo-600 fill-indigo-600 flex-shrink-0" />}
          <span className={`text-sm font-medium truncate ${isBase ? 'text-slate-400 italic' : 'text-slate-900'}`}>
            {row.offer_title || 'Aucune offre'}
          </span>
        </div>
        <span className={`text-base font-black tabular-nums ${isBest && !isBase ? 'text-indigo-700' : 'text-slate-900'}`}>
          {fmtPrice(row.effective_unit_price)}/u
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-2">
        <div>
          <span className="text-slate-400">Qt&eacute; Totale</span>
          <span className="ml-1 font-medium text-slate-700">{row.qty_total}</span>
          {row.qty_sub && <span className="block text-emerald-600 text-[10px]">{row.qty_sub}</span>}
        </div>
        <div>
          <span className="text-slate-400">Mnt Total</span>
          <span className="ml-1 font-medium text-slate-700">{fmtPrice(row.mnt_total)}</span>
          {row.mnt_sub && <span className="block text-red-500 text-[10px]">{row.mnt_sub}</span>}
        </div>
      </div>
      {comp.partner_is_commercial !== false ? (
        <button type="button" onClick={handleAdd}
          disabled={adding || !qtyValid}
          className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-colors
            ${qtyValid ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
          data-testid={`sim-mobile-add-row-${row.mechanic_id || 'base'}`}>
          {adding ? <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full" /> : <Calculator className="h-3 w-3" />}
          Ajouter
        </button>
      ) : (
        <p className="text-center text-[9px] text-slate-400 italic py-1">Non partenaire commercial</p>
      )}
    </div>
  );
};

export default ComparisonTable;
